import type { PaginationState } from "@tanstack/react-table"
import { t } from "@lingui/core/macro"
import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CalendarRange, ChevronDown, Filter, RefreshCcw, UserRound } from "lucide-react"
import { useMemo } from "react"
import { Button } from "@/components/ui/button"
import { DataTable2 } from "@/components/ui/datatable2"
import { FilterCard } from "@/components/ui/FilterCard"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/ui/PageHeader"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { createAuditColumns } from "@/features/audit-logs/components/auditColumns"
import { getAuditLogs } from "@/features/audit-logs/server/auditQueries"
import {
  auditLogQuerySchema,
  type AuditLogFilters,
  type AuditLogResponse,
} from "@/features/audit-logs/types/auditTypes"
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, useFilters } from "@/hooks/useFilters"
import { useLanguage } from "@/i18n/i18n"
import { auditLogQueryKeys } from "@/lib/queryKeys"

const auditLogQuery = (filters: AuditLogFilters) =>
  queryOptions<AuditLogResponse>({
    queryKey: auditLogQueryKeys.list(filters),
    queryFn: () => getAuditLogs({ data: filters }),
    placeholderData: keepPreviousData,
  })

const ALL_USERS_VALUE = "__all_users__"
const ALL_ENTITIES_VALUE = "__all_entities__"

export const Route = createFileRoute("/(authenticated)/settings/audit/")({
  validateSearch: auditLogQuerySchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.ensureQueryData(auditLogQuery(deps))
  },
  component: AuditLogPage,
})

function AuditLogPage() {
  const { filters, setFilters, resetFilters } = useFilters(Route.id)
  const { data, isFetching, error } = useQuery<AuditLogResponse>(auditLogQuery(filters))
  const [language] = useLanguage()

  // Show error if query failed
  if (error) {
    return <div className="p-4 text-red-600">Error: {error.message}</div>
  }

  // Show error if permission denied (return-based error handling)
  if (data && !data.success) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600 font-medium text-lg">{t`Access Denied`}</div>
        <p className="text-neutral-600 mt-2">{data.error}</p>
      </div>
    )
  }

  // At this point, data is either undefined (loading) or has success: true
  const successData = data?.success ? data : null

  const paginationState: PaginationState = {
    pageIndex: filters.pageIndex ?? DEFAULT_PAGE_INDEX,
    pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
  }

  const columns = useMemo(() => createAuditColumns({ language }), [language])

  return (
    <div className="flex-1 space-y-6 overflow-y-auto pb-8">
      <PageHeader
        title={t`Audit activity`}
        description={t`Track who did what across projects and other entities.`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant={filters.action === "project.create.bulkEdit" ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setFilters({
                  action:
                    filters.action === "project.create.bulkEdit"
                      ? undefined
                      : "project.create.bulkEdit",
                  pageIndex: DEFAULT_PAGE_INDEX,
                })
              }
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              {t`Show only bulk edits`}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => resetFilters()} className="gap-2">
              <RefreshCcw className="h-4 w-4" />
              {t`Reset filters`}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FilterCard
          label={t`User`}
          icon={<UserRound className="h-4 w-4" />}
          content={
            <Select
              value={filters.userId && filters.userId.length > 0 ? filters.userId : ALL_USERS_VALUE}
              onValueChange={(value) =>
                setFilters({
                  userId: value === ALL_USERS_VALUE ? undefined : value,
                  pageIndex: DEFAULT_PAGE_INDEX,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t`All users`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_USERS_VALUE}>{t`All users`}</SelectItem>
                {(successData?.filterOptions.users ?? [])
                  .filter((user): user is { id: string; display: string | null } =>
                    Boolean(user.id),
                  )
                  .map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          }
        />

        <FilterCard
          label={t`Entity type`}
          icon={<ChevronDown className="h-4 w-4" />}
          content={
            <Select
              value={
                filters.entityType && filters.entityType.length > 0
                  ? filters.entityType
                  : ALL_ENTITIES_VALUE
              }
              onValueChange={(value) =>
                setFilters({
                  entityType: value === ALL_ENTITIES_VALUE ? undefined : value,
                  pageIndex: DEFAULT_PAGE_INDEX,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t`All entities`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ENTITIES_VALUE}>{t`All entities`}</SelectItem>
                {(successData?.filterOptions.entityTypes ?? []).map((entityType) => (
                  <SelectItem key={entityType} value={entityType}>
                    {entityType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <FilterCard
          label={t`Start date`}
          icon={<CalendarRange className="h-4 w-4" />}
          content={
            <Input
              type="date"
              value={filters.from ?? ""}
              onChange={(event) =>
                setFilters({
                  from: event.target.value || undefined,
                  pageIndex: DEFAULT_PAGE_INDEX,
                })
              }
            />
          }
        />
        <FilterCard
          label={t`End date`}
          icon={<CalendarRange className="h-4 w-4" />}
          content={
            <Input
              type="date"
              value={filters.to ?? ""}
              onChange={(event) =>
                setFilters({
                  to: event.target.value || undefined,
                  pageIndex: DEFAULT_PAGE_INDEX,
                })
              }
            />
          }
        />
        <FilterCard
          label={t`Entity id`}
          icon={<Filter className="h-4 w-4" />}
          content={
            <Input
              value={filters.entityId ?? ""}
              onChange={(event) =>
                setFilters({
                  entityId: event.target.value || undefined,
                  pageIndex: DEFAULT_PAGE_INDEX,
                })
              }
              placeholder={t`Project id, store id...`}
            />
          }
        />
      </div>

      <div className="border-neutral-150 overflow-hidden rounded-lg border bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-900/50">
        <DataTable2
          columns={columns}
          data={successData?.events ?? []}
          rowCount={successData?.totalCount ?? 0}
          enableSorting={false}
          paginationState={paginationState}
          onPaginationChange={(newPagination) =>
            setFilters({
              pageIndex: newPagination.pageIndex,
              pageSize: newPagination.pageSize,
            })
          }
          enablePagination
          isFetching={isFetching}
        />
      </div>
    </div>
  )
}
